import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { X, Plus, Trash2, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { createSharedMailbox } from '../../services/api';

interface CreateSharedMailboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  onCreated: () => void;
}

export default function CreateSharedMailboxModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  onCreated,
}: CreateSharedMailboxModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [alias, setAlias] = useState('');
  const [owners, setOwners] = useState<string[]>([]);
  const [newOwner, setNewOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setDisplayName('');
    setEmailAddress('');
    setAlias('');
    setOwners([]);
    setNewOwner('');
    setError(null);
  }

  function handleClose() {
    if (!loading) {
      resetForm();
      onClose();
    }
  }

  function addOwner() {
    if (newOwner && newOwner.includes('@') && !owners.includes(newOwner)) {
      setOwners([...owners, newOwner]);
      setNewOwner('');
    }
  }

  function removeOwner(email: string) {
    setOwners(owners.filter((o) => o !== email));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Validate
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (!emailAddress.trim() || !emailAddress.includes('@')) {
      setError('Valid email address is required');
      return;
    }

    setLoading(true);

    // Show immediate feedback toast
    const toastId = toast.loading(`Creating mailbox "${displayName.trim()}"... This may take 1-2 minutes.`);

    // Close modal immediately so user can continue working
    resetForm();
    onClose();

    try {
      const response = await createSharedMailbox(clientId, {
        displayName: displayName.trim(),
        emailAddress: emailAddress.trim(),
        alias: alias.trim() || undefined,
        owners: owners.length > 0 ? owners : undefined,
      });

      toast.dismiss(toastId);

      if (response.success) {
        toast.success(response.message || `Shared mailbox "${displayName.trim()}" created successfully!`, { duration: 5000 });

        if (response.ownerErrors && response.ownerErrors.length > 0) {
          toast.error(`Some owners could not be added: ${response.ownerErrors.join(', ')}`, { duration: 8000 });
        }

        onCreated(); // Trigger refresh
      } else {
        toast.error(response.error || 'Failed to create shared mailbox', { duration: 8000 });
      }
    } catch (err) {
      toast.dismiss(toastId);
      const errorMsg = err instanceof Error ? err.message : 'Failed to create shared mailbox';

      // Check if it's a timeout - mailbox may still be creating
      if (errorMsg.includes('timed out') || errorMsg.includes('timeout') || errorMsg.includes('still be')) {
        toast.loading(`Mailbox "${displayName.trim()}" is being created in the background...`, {
          id: 'mailbox-creating',
          duration: 30000
        });
        // Trigger refresh anyway - polling will pick up the new mailbox when ready
        setTimeout(() => {
          toast.dismiss('mailbox-creating');
          toast.success('Check the Mailboxes tab - the mailbox should appear once created.', { duration: 5000 });
          onCreated();
        }, 30000);
      } else {
        toast.error(errorMsg, { duration: 8000 });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 [&>button]:hidden">
        {/* Header */}
        <div className="bg-muted px-4 py-4 sm:px-6 border-b border-border rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Create Shared Mailbox
                </DialogTitle>
                <DialogDescription className="sr-only">Create a new shared mailbox with optional owners and permissions</DialogDescription>
                <p className="text-sm text-muted-foreground">{clientName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-4 sm:px-6 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Sales Team"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              />
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="e.g., sales@company.com"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              />
            </div>

            {/* Alias (Optional) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Alias <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g., sales (auto-generated from email if empty)"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              />
            </div>

            {/* Owners */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Initial Owners <span className="text-muted-foreground">(optional)</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Users who will have Full Access and Send As permissions
              </p>

              {/* Add Owner Input */}
              <div className="flex gap-2 mb-2">
                <input
                  type="email"
                  value={newOwner}
                  onChange={(e) => setNewOwner(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOwner())}
                  placeholder="user@company.com"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={addOwner}
                  disabled={loading || !newOwner || !newOwner.includes('@')}
                  className="px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-accent disabled:opacity-50"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>

              {/* Owner List */}
              {owners.length > 0 && (
                <ul className="space-y-1">
                  {owners.map((owner) => (
                    <li
                      key={owner}
                      className="flex items-center justify-between px-3 py-2 bg-muted rounded-lg"
                    >
                      <span className="text-sm text-foreground">{owner}</span>
                      <button
                        type="button"
                        onClick={() => removeOwner(owner)}
                        disabled={loading}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-muted px-4 py-3 sm:px-6 border-t border-border flex justify-end gap-3 rounded-b-lg">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !displayName || !emailAddress}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {loading ? 'Creating...' : 'Create Mailbox'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
